import { lazy, useEffect, useRef, useState } from "react";
import { Provider, useSelector } from "react-redux";
import { BrowserRouter, createRoutesFromChildren, matchRoutes, Navigate, Route, Routes, useLocation, useNavigationType } from "react-router-dom";
import { Auth } from "@aws-amplify/auth";
import { Amplify } from "@aws-amplify/core";
import CssBaseline from "@mui/material/CssBaseline";
import Grow from "@mui/material/Grow";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import * as Sentry from "@sentry/react";
import { LicenseManager } from "ag-grid-enterprise";
import { ConfigCatProvider } from "configcat-react";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isToday from "dayjs/plugin/isToday";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { SnackbarProvider } from "notistack";
import pMinDelay from "p-min-delay";

import FondAdminRoot from "fond-admin/AdminRoot";

import CreateAccount from "fond/accounts/CreateAccount";
import { withEntityGuard } from "fond/accounts/withEntityGuard";
import withUserInfo from "fond/accounts/withUserInfo";
import { setAnalyticsItem } from "fond/analytics/storage";
import { useGetFolderQuery, useGetMultiProjectQuery, useGetProjectQuery, useGetReportQuery, useGetVersionQuery } from "fond/api";
import { selectCurrentAccount } from "fond/api/accountSlice";
import { selectUserId } from "fond/api/userSlice";
import { useGetWorkflowsQuery } from "fond/api/workflowSlice";
import { CityPlannerPage } from "fond/cityPlanner/CityPlannerPage";
import { init as cognitoInit } from "fond/cognito/redux";
import { CrossTabLogoutListener } from "fond/components/CrossTabLogoutListener";
import { StaleAccountModal } from "fond/components/StaleAccountModal";
import { useFeatureFlag } from "fond/featureFlags";
import ForgotPassword from "fond/forgotPassword/ForgotPassword";
import ForgotPasswordSubmit from "fond/forgotPassword/ForgotPasswordSubmit";
import { Impersonate } from "fond/impersonate";
import ImportControllers from "fond/import/controller/ImportControllers";
import InvalidInvitation from "fond/invitation/InvalidInvitation";
import Invitation from "fond/invitation/Invitation";
import { Popout } from "fond/layout";
import Notifier from "fond/notifications/Notifier";
import PrivateRoute from "fond/PrivateRoute";
import PrivateRouteWrapper from "fond/PrivateRouteWrapper";
import ProjectPage from "fond/project/ProjectPage";
import { selectCurrentAccountId } from "fond/redux/accountContextSlice";
import SignIn from "fond/signIn/SignIn";
import SignOut from "fond/signIn/SignOut";
import SignUp from "fond/signUp/SignUp";
import VerifyEmail from "fond/signUp/VerifyEmail";
import { snackbarComponentVariantMap } from "fond/snackbar";
import store from "fond/store";
import { StyleEditorPage } from "fond/styleEditor";
import theme from "fond/theme";
import { UserRoles } from "fond/types";
import { initializeAccountContext, setAccountContext } from "fond/utils/accountContextStorage";
import { useAppSelector } from "fond/utils/hooks";
import { setupFilterConsoleWarnings } from "fond/utils/logging";
import { isAppStageIn } from "fond/utils/stages";
import { AccountManagement, Insights, ProfileManagement, UserManagement } from "fond/views";
import Accounts from "fond/views/Accounts/Accounts";
import CityPlannerFolder from "fond/views/CityPlannerFolder/CityPlannerFolder";
import CityReportFolder from "fond/views/CityReportFolder/CityReportFolder";
import GenericErrorPage from "fond/views/Error/GenericErrorPage";
import NoMatch from "fond/views/Error/NoMatch";
import ResourceNotFound from "fond/views/Error/ResourceNotFound";
import Folders from "fond/views/Folders/Folders";
import MainWorkspace from "fond/views/MainWorkspace/MainWorkspace";
import Recent from "fond/views/Recent/Recent";
import { CreateReport, ImportReport, ReportCost, ReportOverview, ReportRevenue, ReportSettings } from "fond/views/Report";
import PollReportStatus from "fond/views/Report/PollReportStatus/PollReportStatus";
import Settings from "fond/views/Settings/Settings";
import SharedWithMe from "fond/views/Shared/SharedWithMe";
import Starred from "fond/views/Starred/Starred";
import SwitchAccount from "fond/views/SwitchAccount/SwitchAccount";
import { SuspenseSpinner } from "fond/widgets/SuspenseSpinner";

import packageJson from "../package.json";

import "./index.scss";

type ReactComponentModule = () => Promise<{ default: React.FunctionComponent }>;

/**
 * Force a minimum delay when loading views. This is to prevent the view from loading too quickly and causing a flicker
 * After the initial load, the view will load immediately.
 */
const lazyLoadView = (importFactory: ReactComponentModule) => {
  return lazy(() => pMinDelay(importFactory(), 500));
};

const HomeView = lazyLoadView(() => import("fond/views/Home"));
const SubscriptionsView = lazyLoadView(() => import("fond/views/Subscription"));

setupFilterConsoleWarnings([
  // A warning thrown by Mapbox GL JS when an image is not found in the map's style.
  // We do follow their advice and use `map.addImage()` to add the image, so this warning is not relevant.
  "Please make sure you have added the image with map.addImage()",
  // A warning thrown by Stripe.js when the integration is not using HTTPS - this is expected in local development.
  "You may test your Stripe.js integration over HTTP.",
]);

// This is a (hopefully) temporary fix to disable warnings from Atlaskit about its use of their feature flag client.
// See also in vitest.setup.ts
// @ts-expect-error disabling this thing so don't care about type errors
window.__CRITERION__ = { getFeatureFlagOverride: () => false }; // eslint-disable-line no-underscore-dangle

/*
 * Setup the aws amplify library with the correct cognito user pool information.
 */
const awsConfig = {
  Auth: {
    userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
    userPoolWebClientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
    // Used for specifying a Cognito endpoint override. Can be left blank for default behavior.
    endpoint: process.env.REACT_APP_COGNITO_ENDPOINT,
    // Used to change the authentication flow type to "USER_PASSWORD_AUTH" for local Cognito simulation, which is
    // needed to prevent the email confirmation flow, as it's inaccessible via Docker. Can be left blank for default
    // behavior.
    authenticationFlowType: process.env.REACT_APP_AUTHENTICATION_FLOW_TYPE,
  },
};

Amplify.configure(awsConfig);
Auth.configure(awsConfig);

// Initialise dayjs settings / plugins
dayjs.extend(utc);
dayjs.extend(isToday);
dayjs.extend(advancedFormat);
dayjs.extend(relativeTime);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);

// Set AgGrid license
LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY!);

// Only allow Sentry if we're NOT running locally or running tests.
const allowSentry = !(isAppStageIn(["local", "test"]) || window.location.hostname === "localhost");
if (allowSentry) {
  Sentry.init({
    dsn: "https://47c3988215ff4f299aebf95570e339e3@o312892.ingest.sentry.io/5227566",
    environment: process.env.REACT_APP_STAGE,
    release: packageJson.version,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    // Capture 1.5% of transaction for performance monitoring.  This is conservative - once we
    // have a better idea of the number of transactions we can bump this rate as appropriate.
    tracesSampleRate: 0.015,
  });
}

/**
 * The following components are wrapped in the `withEntityGuard` HOC which checks if the entity being accessed
 * belongs to a different account and if the user has access to that account.
 * If so, it redirects to the switch account page. If not, it will redirect to a 404 page.
 */
const FoldersGuarded = withEntityGuard(Folders, "folderId", useGetFolderQuery, "FOLDER");
const CityPlannerFolderGuarded = withEntityGuard(CityPlannerFolder, "multiProjectFolderId", useGetFolderQuery, "FOLDER");
const CityReportFolderGuarded = withEntityGuard(CityReportFolder, "multiReportFolderId", useGetFolderQuery, "FOLDER");
const CityPlannerPageGuarded = withEntityGuard(CityPlannerPage, "cityId", useGetMultiProjectQuery, "MULTI_PROJECT");
const ProjectPageGuarded = withEntityGuard(ProjectPage, "uuid", useGetProjectQuery, "PROJECT");
const ReportSettingsGuarded = withEntityGuard(ReportSettings, "reportId", useGetReportQuery, "REPORT");
const ReportOverviewGuarded = withEntityGuard(ReportOverview, "reportId", useGetReportQuery, "REPORT");
const ReportCostGuarded = withEntityGuard(ReportCost, "reportId", useGetReportQuery, "REPORT");
const ReportRevenueGuarded = withEntityGuard(ReportRevenue, "reportId", useGetReportQuery, "REPORT");
const PollReportStatusGuarded = withEntityGuard(PollReportStatus, "reportId", useGetReportQuery, "REPORT");
const StyleEditorGuarded = withEntityGuard(StyleEditorPage, "uuid", useGetVersionQuery, "PROJECT");

/**
 * The root of the application that is mounted after the providers have been
 * mounted.  This allows the content to only be rendered once we have initialised
 * cognito (which requires redux to already be mounted)
 */
const Root = () => {
  const { value: isFondAdminEnabled } = useFeatureFlag("fond_admin");

  const userId = useAppSelector(selectUserId);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const hasInitializedAccountContext = useRef(false);

  // Initialize account context from localStorage on first load
  useEffect(() => {
    if (userId && !hasInitializedAccountContext.current) {
      initializeAccountContext(userId);
      hasInitializedAccountContext.current = true;
    }
  }, [currentAccount, userId]);

  // Store the current user's ID in LS so analytics has access to it
  // Only sync account context after initialization to avoid race condition
  useEffect(() => {
    if (!userId || !currentAccount?.ID || !hasInitializedAccountContext.current) {
      hasInitializedAccountContext.current = false;
      return;
    }

    setAnalyticsItem("userId", userId);
    setAccountContext(userId, currentAccount?.ID);
  }, [userId, currentAccount?.ID]);

  const [init, setInit] = useState(false);

  useEffect(() => {
    // Wait for Cognito to initialise, which includes getting the user's
    // attributes (eg. first and last names), if they're logged in, before trying
    // to render anything.
    async function initUser() {
      await cognitoInit(store);
      setInit(true);
    }
    initUser();
  }, []);

  if (!init) return null;

  const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

  return (
    <SnackbarProvider
      maxSnack={1}
      TransitionComponent={Grow}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      Components={snackbarComponentVariantMap}
    >
      <StaleAccountModal />
      <CrossTabLogoutListener />
      <Notifier />
      <BrowserRouter future={{ v7_startTransition: false, v7_relativeSplatPath: true }}>
        {/* Render the ImportControllers at top level to support status tracking of imports from any page */}
        <ImportControllers />
        <SentryRoutes>
          <Route path="/" Component={PrivateRouteWrapper}>
            <Route index Component={withUserInfo(HomeView)} />
          </Route>

          <Route path="/" element={<PrivateRoute component={withUserInfo(MainWorkspace)} />} />
          <Route path="/workspace" element={<PrivateRoute component={withUserInfo(MainWorkspace)} />} />
          <Route path="/shared" element={<PrivateRoute component={withUserInfo(SharedWithMe)} />} />
          <Route path="/shared/:account" Component={withUserInfo(SharedWithMe)} />
          <Route path="/recent" element={<PrivateRoute component={withUserInfo(Recent)} />} />
          <Route path="/starred" element={<PrivateRoute component={withUserInfo(Starred)} />} />
          <Route path="/folders/:folderId" element={<PrivateRoute component={withUserInfo(FoldersGuarded)} />} />

          <Route path="/accounts" element={<PrivateRoute component={withUserInfo(Accounts)} />} />

          <Route path="/cities/:multiProjectFolderId" element={<PrivateRoute component={withUserInfo(CityPlannerFolderGuarded)} />} />
          <Route path="/city/report/:multiReportFolderId" element={<PrivateRoute component={withUserInfo(CityReportFolderGuarded)} />} />
          <Route path="/city/:cityId" element={<PrivateRoute component={withUserInfo(CityPlannerPageGuarded)} />} />

          <Route path="/project/:uuid/" element={<PrivateRoute component={withUserInfo(ProjectPageGuarded)} />}>
            <Route path=":type/:id/" element={<PrivateRoute component={withUserInfo(ProjectPageGuarded)} />} />
          </Route>

          <Route path="/reports/create" element={<PrivateRoute component={withUserInfo(CreateReport)} />} />
          <Route path="/reports/import" element={<PrivateRoute component={withUserInfo(ImportReport)} />} />
          <Route path="/reports/:reportId">
            <Route path="settings" element={<PrivateRoute component={withUserInfo(ReportSettingsGuarded)} />} />
            <Route path="overview" element={<PrivateRoute component={withUserInfo(ReportOverviewGuarded)} />} />
            <Route path="cost" element={<PrivateRoute component={withUserInfo(ReportCostGuarded)} />} />
            <Route path="revenue" element={<PrivateRoute component={withUserInfo(ReportRevenueGuarded)} />} />
            <Route path="poll" element={<PrivateRoute component={withUserInfo(PollReportStatusGuarded)} />} />
            <Route index element={<Navigate to="overview" replace />} />
          </Route>

          <Route path="/settings" element={<PrivateRoute component={withUserInfo(Settings)} />} />
          <Route path="/settings/profile/*" element={<PrivateRoute component={withUserInfo(ProfileManagement)} />} />
          <Route path="/styles/:uuid" element={<PrivateRoute component={withUserInfo(StyleEditorGuarded)} />} />
          <Route path="/settings/account/*" element={<PrivateRoute component={withUserInfo(AccountManagement, UserRoles.admin)} />} />
          <Route path="/settings/users/*" element={<PrivateRoute component={withUserInfo(UserManagement, UserRoles.admin)} />} />
          <Route path="/settings/insights" element={<PrivateRoute component={withUserInfo(Insights, UserRoles.admin)} />} />

          <Route path="/subscription/*" element={<PrivateRoute component={withUserInfo(SubscriptionsView, UserRoles.admin)} />} />

          {isFondAdminEnabled && <Route path="/admin/*" element={<PrivateRoute component={withUserInfo(FondAdminRoot)} />} />}

          <Route path="/signout" element={<SignOut />} />
          <Route path="/signin" element={<SignIn />} />
          {/* old route, just keeping around for old links */}
          <Route path="/login" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/verifyemail" element={<VerifyEmail />} />
          <Route path="/forgotpassword" element={<ForgotPassword />} />
          <Route path="/forgotpasswordsubmit" element={<ForgotPasswordSubmit />} />
          <Route path="/createaccount" element={<PrivateRoute component={withUserInfo(CreateAccount)} />} />
          <Route path="/invitations">
            <Route path="invalid" element={<InvalidInvitation />} />
            <Route path=":invitationId" element={<Invitation />} />
            <Route index element={<NoMatch />} />
          </Route>
          <Route path="/impersonate/:email" element={<Impersonate />} />
          <Route path="/widget" element={<Popout />} />
          {/* catch all route which sends the user to a 'page not found' component */}
          <Route path="/error" element={<GenericErrorPage />} />
          <Route path="/404" element={<ResourceNotFound />} />
          <Route path="/switchaccount" element={<SwitchAccount />} />
          <Route path="*" element={<NoMatch />} />
        </SentryRoutes>
      </BrowserRouter>
    </SnackbarProvider>
  );
};

/**
 * A renderless component to place workflow polling outside of the root application to avoid unnecessary rerenders
 */
const WorkflowPoller: React.FC = () => {
  const baseInterval = 10_000;
  const increment = 5_000;
  const maxInterval = 30_000;
  const [fetchCount, setFetchCount] = useState(0);
  const [pollingInterval, setPollingInterval] = useState(baseInterval);
  const currentAccountId = useSelector(selectCurrentAccountId);
  const { data, isFetching } = useGetWorkflowsQuery(undefined, { pollingInterval, refetchOnMountOrArgChange: true, skip: !currentAccountId });

  // When the data changes (which is memoised by RTK, so we can reliably use this simple useEffect method)
  // then we reset the fetch count and polling interval.
  useEffect(() => {
    setFetchCount(0);
    setPollingInterval(baseInterval);
  }, [data]);

  // When a fetch happens, and no new data was fetched, we increase the space between requests
  useEffect(() => {
    if (isFetching) {
      const newFetchCount = fetchCount + 1;
      const newInterval = baseInterval + increment * newFetchCount;
      setFetchCount((currentCount) => currentCount + 1);
      setPollingInterval(Math.min(newInterval, maxInterval));
    }
    // We do not want fetch count in the deps since we're updating it in this call
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isFetching]);
  return null;
};

export const App = (): JSX.Element => (
  <StyledEngineProvider injectFirst>
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CssBaseline />
        <Provider store={store}>
          <ConfigCatProvider sdkKey={process.env.REACT_APP_CONFIG_CAT_KEY!}>
            <SuspenseSpinner name="root">
              <Root />
              <WorkflowPoller />
            </SuspenseSpinner>
          </ConfigCatProvider>
        </Provider>
      </LocalizationProvider>
    </ThemeProvider>
  </StyledEngineProvider>
);
